import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import CreateCardPage from "@/pages/CreateCardPage";
import EditCardPage from "@/pages/EditCardPage";
import PublicCardPage from "@/pages/PublicCardPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import PrintPreviewPage from "@/pages/PrintPreviewPage";
import ScanHistoryPage from "@/pages/ScanHistoryPage";
import OrderPage from "@/pages/OrderPage";
import OrdersPage from "@/pages/OrdersPage";
import HowToGuidePage from "@/pages/HowToGuidePage";
import QRCustomizerPage from "@/pages/QRCustomizerPage";
import NFCSetupPage from "@/pages/NFCSetupPage";
import "@/App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Landing Page */}
            <Route path="/" element={<LandingPage />} />
            
            {/* Public Routes */}
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
            <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
            <Route path="/card/:cardId" element={<PublicCardPage />} />
            
            {/* Protected Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/cards/new" element={<ProtectedRoute><CreateCardPage /></ProtectedRoute>} />
            <Route path="/cards/:cardId/edit" element={<ProtectedRoute><EditCardPage /></ProtectedRoute>} />
            <Route path="/cards/:cardId/print" element={<ProtectedRoute><PrintPreviewPage /></ProtectedRoute>} />
            <Route path="/cards/:cardId/history" element={<ProtectedRoute><ScanHistoryPage /></ProtectedRoute>} />
            <Route path="/cards/:cardId/order" element={<ProtectedRoute><OrderPage /></ProtectedRoute>} />
            <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
            <Route path="/analytics/:cardId" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
            <Route path="/guide" element={<ProtectedRoute><HowToGuidePage /></ProtectedRoute>} />
            <Route path="/cards/:cardId/qr-customizer" element={<ProtectedRoute><QRCustomizerPage /></ProtectedRoute>} />
            <Route path="/cards/:cardId/nfc" element={<ProtectedRoute><NFCSetupPage /></ProtectedRoute>} />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
